// Block 1 (owner 2026-10-08): ONE rendering of the order's invoicing position, shared by the order's Invoices tab and the Lines-tab panel so the two cannot drift.
// Every figure is a server field (invoicing read position + status snapshot); this file only lays them out. Money = exact 2-dp text.
import React from 'react';
import { formatMoneyExact } from '../../utils/format';
import { cents, reasonKey, tieState } from '../../pages/Sales/invoicing/invoicingModel.mjs';

const M = v => formatMoneyExact(v);

// "Delivered X - returned Y = recognised Z. Invoiced A - credit notes B = C. Over-invoiced D." + the ties verdict
export function TieOut({ P, t, testid = 'inv-ties' }) {
  const st = tieState(P);
  const extra = cents(P.draft) !== 0n || cents(P.invoiceable_now) !== 0n || cents(P.not_delivered) !== 0n;
  return (
    <div className={`inv-pos-tie ${st === 'ok' ? 'ok' : 'bad'}`} data-testid={testid} data-tie={st}><span className="inv-pos-dot" aria-hidden="true" /><span>
      {t(cents(P.discounts_recognised) !== 0n ? 'tieNetDisc' : 'tieNet', { delivered: M(P.delivered), returned: M(P.returned), recognised: M(P.recognised), discounts: M(P.discounts_recognised), billable: M(P.billable_recognised), invoiced: M(P.invoiced), creditNotes: M(P.credit_notes), netInvoiced: M(P.net_invoiced), over: M(P.over_invoiced) })}
      {extra && <> {t('tieRest', { draft: M(P.draft), invoiceable: M(P.invoiceable_now), notDelivered: M(P.not_delivered) })}</>}
      {' '}{st === 'ok' ? <b>{t('ties')} · Δ {M(P.ties_delta)}</b> : st === 'over' ? <b>{t('tieOver', { amount: M(P.over_invoiced) })}</b> : <b>{t('tieBad', { delta: M(P.ties_delta) })}</b>}
    </span></div>
  );
}

// the two headlines side by side: "Invoiceable now" (+ the status badge) and, when over-invoiced, "Credit note needed" (warn)
export function Headlines({ P, snapshot, badge, why, t }) {
  const over = cents(P.over_invoiced) > 0n;
  return (
    <div className="inv-pos-heads" data-testid="inv-heads">
      <div className="inv-pos-head" data-testid="inv-head-invoiceable"><span className="kl">{t('invoicableTitle')}</span>{badge}
        <span className="k-val" data-testid="inv-invoiceable-val">{M(P.invoiceable_now)}</span></div>
      {over && <div className="inv-pos-head warn" data-testid="inv-head-cn"><span className="kl">{t('creditNoteNeeded')}</span><span className="k-val">{M(P.over_invoiced)}</span></div>}
      {why && <span className="k-sub" data-testid="order-invoicable-reason">{why}</span>}
    </div>
  );
}

// Lines-tab panel body (lean = invoicing rows only; Delivered/Returned/Recognised/Discounts already sit in the Order summary + Order totals panel): the same fields as rows (Delivered, Returned, Recognised, Invoiced, Credit notes, Net invoiced, Over-invoiced, Invoiceable now) + the SAME tie-out line
export function PositionSummary({ P, snapshot, t, lean }) {
  const row = (k, v, o = {}) => <tr key={k} style={o.top ? { borderTop: '2px solid var(--line)' } : undefined}><td style={{ color: o.bold ? undefined : 'var(--muted)', fontWeight: o.bold ? 700 : undefined, paddingLeft: o.indent ? 14 : undefined }}>{t(k)}</td>
    <td className={`td-r num${o.bold ? ' b' : ''}`} style={o.color ? { color: o.color } : undefined}>{M(v)}</td></tr>;
  const hasRet = cents(P.returned) !== 0n, hasCn = cents(P.credit_notes) !== 0n, over = cents(P.over_invoiced) > 0n;
  return (
    <>
      <table><tbody>
        {!lean && row('sumDelivered', P.delivered)}
        {!lean && hasRet && row('sumReturned', P.returned, { indent: true, color: 'var(--red)' })}
        {!lean && row('sumRecognised', P.recognised, { bold: true, top: true })}
        {!lean && cents(P.discounts_recognised) !== 0n && row('sumDiscounts', P.discounts_recognised, { indent: true, color: 'var(--red)' })}
        {cents(P.discounts_recognised) !== 0n && row('sumBillable', P.billable_recognised)}
        {row('sumInvoiced', P.invoiced)}
        {hasCn && row('sumCreditNotes', P.credit_notes, { indent: true, color: 'var(--red)' })}
        {hasCn && row('sumNetInvoiced', P.net_invoiced)}
        {cents(P.draft) !== 0n && row('sumOnDraft', P.draft, { indent: true })}
        {row('sumInvoiceable', P.invoiceable_now)}
        {over && row('sumOver', P.over_invoiced, { bold: true, top: true, color: 'var(--red)' })}
      </tbody></table>
      <TieOut P={P} t={t} testid="sum-ties" />
    </>
  );
}

export { reasonKey };
